import { confirmUnsavedChanges } from '@/shared/lib/confirmUnsavedChanges';
import { useUnsavedForm } from '@/shared/hooks/useUnsavedForm';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useOrders } from '@/entities/Order/model/store';
import {
    getManualPaymentConfirmDefaults,
    manualPaymentConfirmSchema,
    type ManualPaymentConfirmFormValues,
} from '@/entities/Payment/model/manualPaymentConfirmSchema';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { useManualConfirmSubmit } from './useManualConfirmSubmit';
import classes from './ManualConfirmModal.module.scss';

export interface ManualConfirmModalProps {
    visible: boolean;
    onHide: () => void;
    orderId: string;
    externalPaymentId: string | null | undefined;
}

const {
    TextField: PaymentUrlField,
    TextField: CommentField,
} = createRHFFields<ManualPaymentConfirmFormValues>();

/** Показывает форму ручного подтверждения оплаты заказа. */
export const ManualConfirmModal = (
    {
        visible,
        onHide,
        orderId,
        externalPaymentId,
    }: ManualConfirmModalProps
) => {
    const form = useUnsavedForm<ManualPaymentConfirmFormValues>({
        resolver: zodResolver(manualPaymentConfirmSchema),
        defaultValues: getManualPaymentConfirmDefaults(externalPaymentId),
    }, visible);

    const { onSubmit, completed, resetCompleted } = useManualConfirmSubmit(form);
    const getOrderById = useOrders((state) => state.getOrderById);
    const { reset } = form;

    useEffect(() => {
        if (!visible) return;

        reset(getManualPaymentConfirmDefaults(externalPaymentId));
        resetCompleted();
    }, [externalPaymentId, visible, reset, resetCompleted]);

    /** Закрывает окно и обновляет заказ после подтверждения оплаты. */
    const handleHide = () => confirmUnsavedChanges(form.formState.isDirty, () => {
        reset();

        if (completed) void getOrderById(orderId);
        onHide();
    });

    const isSubmitting = form.formState.isSubmitting;
    const hasExternalId = Boolean(externalPaymentId?.trim());

    return <Modal
        visible={visible}
        onHide={handleHide}
        title="Подтвердить оплату за заказ"
        size="lg"
        className={classes.modal}
        closable={!isSubmitting}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label={completed
                    ? 'Закрыть'
                    : 'Отмена'
                }
                text
                onClick={handleHide}
                disabled={isSubmitting}
            />
            {!completed && hasExternalId &&
                <Button
                    type="submit"
                    form="manual-payment-confirm-form"
                    label="Подтвердить оплату"
                    icon="pi pi-check"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                />
            }
        </div>}
    >
        {!hasExternalId &&
            <RequestEmpty message="Номер платежа в Robokassa не найден" />
        }
        {hasExternalId && completed &&
            <Message
                severity="success"
                text="Оплата за заказ успешно подтверждена"
            />
        }
        {hasExternalId && !completed &&
            <FormProvider {...form}>
                <form
                    id="manual-payment-confirm-form"
                    onSubmit={form.handleSubmit(onSubmit)}
                    className={classes.form}
                    noValidate
                >
                    <PaymentUrlField
                        name="robokassaPaymentUrl"
                        label="Ссылка на платеж в Робокасса, по которой производилась оплата"
                        placeholder="Заполните ссылку, по которой была произведена оплата"
                        disabled={isSubmitting}
                        isRequired
                    />
                    <CommentField
                        name="comment"
                        label="Комментарий"
                        placeholder="Комментарий"
                        disabled={isSubmitting}
                    />
                    {isSubmitting &&
                        <RequestLoading message="Подтверждаем оплату..." />
                    }
                    {form.formState.errors.root?.server?.message &&
                        <RequestError
                            message={form.formState.errors.root.server.message}
                            compact
                        />
                    }
                </form>
            </FormProvider>
        }
    </Modal>;
};
